import type { EnvironmentType } from "@taskfortress/core"
import { Analytics } from "@taskfortress/core"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import type { ReactNode } from "react"
import env from "../env"

// Variable to track initialization status
let isAnalyticsInitialized = false

// Define the context type
interface AnalyticsContextType {
  trackPageView: (title: string, url: string) => void
  trackEvent: (
    category: string,
    action: string,
    name?: string,
    value?: number,
  ) => void
  setUserId: (userId: string) => void
}

// Create the context
const AnalyticsContext = createContext<AnalyticsContextType | null>(null)

// Hook to use the analytics context
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider")
  }
  return context
}

interface AnalyticsProviderProps {
  children: ReactNode
}

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null)

  useEffect(() => {
    // Use existing instance or create a new one if not initialized
    if (!isAnalyticsInitialized) {
      const instance = new Analytics({
        enabled: env.analytics.enabled,
        url: env.analytics.matomoUrl,
        siteId: env.analytics.matomoSiteId,
        environment: env.analytics.environment as EnvironmentType,
        windowObj: window,
      })
      setAnalytics(instance)
      isAnalyticsInitialized = true // Mark as initialized
    }
  }, [])

  const trackPageView = useCallback(
    (title: string, url: string) => {
      if (analytics) {
        analytics.trackPageView(title, url)
      }
    },
    [analytics],
  )

  const trackEvent = useCallback(
    (category: string, action: string, name?: string, value?: number) => {
      if (analytics) {
        analytics.trackEvent(
          category,
          action,
          window.location.href,
          name,
          value,
        )
      }
    },
    [analytics],
  )

  const setUserId = useCallback(
    (userId: string) => {
      if (analytics) {
        analytics.setUserId(userId)
      }
    },
    [analytics],
  )

  const value = {
    trackPageView,
    trackEvent,
    setUserId,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}
